import React, { useState, useEffect, useRef } from 'react';
import { useFeatures } from '../hooks/useFeatures';
import { Youtube, User, Users, Menu, X, ChevronDown, ScrollText, Scale, FileText, Calculator, Briefcase, Building2, LogOut, LayoutDashboard, FolderOpen, Shield, Download, MapPin, ClipboardList, CreditCard, ClipboardCheck, ScanLine, Siren, PhoneCall, CalendarClock, Wallet, Inbox } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import { SchliesszeitBalken } from './SchliesszeitHinweis';

const Logo = () => (
  <div className="flex items-center">
    <img
      src="/assets/logo.png"
      alt="Kanzlei Nabhan Gäbler und Partner"
      className="lf-logo-img h-20 lg:h-24 w-auto object-contain"
    />
  </div>
);

const rechtsgebieteItems = [
  {
    key: 'einbuergerungs_selbsttest', label: 'nav.rechtsgebiete_items.einbuergerungs_selbsttest.label',
    href: '/einbuergerungs-selbsttest',
    icon: ScrollText,
    badge: 'NEU',
    desc: 'nav.rechtsgebiete_items.einbuergerungs_selbsttest.desc',
  },
  { key: 'auslaenderrecht', label: 'nav.rechtsgebiete_items.auslaenderrecht.label', href: '/rechtsgebiete/auslaenderrecht', icon: FileText, desc: 'nav.rechtsgebiete_items.auslaenderrecht.desc' },
  { key: 'strafrecht', label: 'nav.rechtsgebiete_items.strafrecht.label', href: '/rechtsgebiete/strafrecht', icon: Scale, desc: 'nav.rechtsgebiete_items.strafrecht.desc' },
  { key: 'steuerberatung', label: 'nav.rechtsgebiete_items.steuerberatung.label', href: '/rechtsgebiete/steuerberatung', icon: Calculator, desc: 'nav.rechtsgebiete_items.steuerberatung.desc' },
  { key: 'steuerrecht', label: 'nav.rechtsgebiete_items.steuerrecht.label', href: '/rechtsgebiete/steuerrecht', icon: Calculator, desc: 'nav.rechtsgebiete_items.steuerrecht.desc' },
  { key: 'gesellschaftsrecht', label: 'nav.rechtsgebiete_items.gesellschaftsrecht.label', href: '/rechtsgebiete/gesellschaftsrecht', icon: Building2, desc: 'nav.rechtsgebiete_items.gesellschaftsrecht.desc' },
  { key: 'verwaltungsrecht', label: 'nav.rechtsgebiete_items.verwaltungsrecht.label', href: '/rechtsgebiete/verwaltungsrecht', icon: Briefcase, desc: 'nav.rechtsgebiete_items.verwaltungsrecht.desc' },
  { key: 'mietrecht', label: 'nav.rechtsgebiete_items.mietrecht.label', href: '/rechtsgebiete/mietrecht', icon: Building2, desc: 'nav.rechtsgebiete_items.mietrecht.desc' },
  { key: 'verkehrsrecht', label: 'nav.rechtsgebiete_items.verkehrsrecht.label', href: '/rechtsgebiete/verkehrsrecht', icon: FileText, desc: 'nav.rechtsgebiete_items.verkehrsrecht.desc' },
];

const allServicesItems = [
  { key: 'selbsttest', label: 'nav.services_items.selbsttest.label', href: '/einbuergerungs-selbsttest', icon: ClipboardCheck, desc: 'nav.services_items.selbsttest.desc' },
  { key: 'downloads', label: 'nav.services_items.downloads.label', href: '/downloads', icon: Download, desc: 'nav.services_items.downloads.desc' },
  { key: 'kontakt', label: 'nav.services_items.kontakt.label', href: '/#kontakt', icon: MapPin, desc: 'nav.services_items.kontakt.desc' },
  { key: 'mandantenerfassung', label: 'nav.services_items.mandantenerfassung.label', href: '/mandantenerfassung', icon: ClipboardList, desc: 'nav.services_items.mandantenerfassung.desc' },
  { portal: true, key: 'sepa', label: 'nav.services_items.sepa.label', href: '/sepa-lastschrift', icon: CreditCard, desc: 'nav.services_items.sepa.desc' },
  { portal: true, key: 'upload', label: 'nav.services_items.upload.label', href: '/dokumente/scannen', icon: ScanLine, desc: 'nav.services_items.upload.desc' },
  { key: 'honorar', label: 'nav.services_items.honorar.label', href: '/honorarrechner', icon: Calculator, desc: 'nav.services_items.honorar.desc' },
  { portal: true, key: 'rueckruf', label: 'nav.services_items.rueckruf.label', href: '/rueckruf', icon: PhoneCall, desc: 'nav.services_items.rueckruf.desc' },
  { portal: true, key: 'raten', label: 'nav.services_items.raten.label', href: '/ratenzahlung', icon: CalendarClock, desc: 'nav.services_items.raten.desc' },
  { key: 'akonto', label: 'nav.services_items.akonto.label', href: '/akontozahlung', icon: Wallet, desc: 'nav.services_items.akonto.desc' },
  { key: 'notfall', label: 'nav.services_items.notfall.label', href: '/notfallkontakt', icon: Siren, desc: 'nav.services_items.notfall.desc', feature: 'notfall' },
];

const topNavLinks = [
  { key: 'start', label: 'START', href: '/' },
  { key: 'team', label: 'TEAM', href: '/team' },
  { key: 'aktuelles', label: 'AKTUELLES', href: '/aktuelles' },
  { key: 'karriere', label: 'KARRIERE', href: '/karriere' },
];

const Header = () => {
  const features = useFeatures();
  const servicesItems = allServicesItems.filter((i) => !i.portal && (!i.feature || features[i.feature]));
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const servicesRef = useRef(null);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { t } = useTranslation();
  const istTeam = ['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(user?.role);
  const menuItems = [
    istTeam ? { id: 'eingang', to: '/kanzlei/eingang', icon: Inbox, label: 'Dashboard' } : { id: 'portal', to: '/dashboard', icon: LayoutDashboard, label: t('nav.portal', 'Portal') },
    { id: 'profil', to: '/profil', icon: User, label: t('nav.profile') },
    ...(user?.role === 'admin' ? [{ id: 'admin', to: '/admin', icon: Shield, label: t('nav.admin') }] : []),
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const initials = (user?.name || user?.email || 'M').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const linkClass = (href) =>
    `text-sm font-medium tracking-wide hover:text-[#b8893d] transition-colors duration-200 relative group ${
      location.pathname === href ? 'text-[#b8893d] lf-nav-aktiv' : 'text-[#1B3A5C]'
    }`;

  const renderNavLink = (link, onClick) => {
    const label = link.key ? t(`nav.${link.key}`).toUpperCase() : link.label;
    const isHash = link.href.includes('#');
    if (isHash) {
      return (
        <a key={link.key || link.label} href={link.href} onClick={onClick} className={linkClass(link.href)}>
          {label}
          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#b8893d] group-hover:w-full transition-all duration-300"></span>
        </a>
      );
    }
    return (
      <Link key={link.key || link.label} to={link.href} onClick={onClick} className={linkClass(link.href)}>
        {label}
        <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#b8893d] group-hover:w-full transition-all duration-300"></span>
      </Link>
    );
  };

  const isRechtsgebieteActive = rechtsgebieteItems.some((i) => i.href.split('?')[0] === location.pathname);

  return (
    <header
      className={`lf-site-header fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled ? 'shadow-[0_10px_30px_-18px_rgba(11,23,36,0.45)]' : 'shadow-none'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="lf-kopfzeile flex items-center justify-between py-3">
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <a
              href="https://www.youtube.com/@KanzleiNabhanGabler"
              data-lf-youtube=""
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-7 bg-red-600 rounded hover:bg-red-700 transition-colors"
              aria-label="YouTube"
            >
              <Youtube className="w-5 h-5 text-white" />
            </a>
            <LanguageSwitcher variant="header" />
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  data-testid="user-menu-trigger"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-[#1B3A5C] hover:text-[#2a4f7a] transition-colors"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-[#b8893d]" />
                  ) : (
                    <span className="w-8 h-8 rounded-full bg-[#b8893d] flex items-center justify-center text-white text-xs font-semibold">
                      {initials}
                    </span>
                  )}
                  <span className="text-sm font-medium max-w-[140px] truncate">{user?.name?.split(' ')[0]}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute top-full right-0 mt-3 w-64 bg-white shadow-2xl border-t-4 border-[#b8893d] rounded-sm overflow-hidden z-50">
                    <div className="bg-[#1B3A5C] text-white px-4 py-3">
                      <p className="text-sm font-medium truncate">{user?.name}</p>
                      <p className="text-xs text-white/70 truncate">{user?.email}</p>
                    </div>
                    <ul className="py-1">
                      {menuItems.map((item) => (
                        <li key={item.to}>
                          <Link
                            to={item.to}
                            onClick={() => setUserMenuOpen(false)}
                            data-testid={`user-menu-${item.id}`}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1B3A5C] hover:bg-[#f5f8fa]"
                          >
                            <item.icon className="w-4 h-4 text-[#b8893d]" />
                            {item.label}
                          </Link>
                        </li>
                      ))}
                      <li className="border-t border-gray-100">
                        <button
                          data-testid="user-menu-logout"
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50"
                        >
                          <LogOut className="w-4 h-4" /> {t('nav.logout')}
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-2 text-[#1B3A5C] hover:text-[#2a4f7a] transition-colors"
                data-testid="header-anmelden"
              >
                <span className="w-8 h-8 rounded-full bg-[#1B3A5C] flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </span>
                <span className="text-sm font-medium">{t('nav.login')}</span>
              </Link>
            )}
          </div>

          <Link
            to={isAuthenticated ? (['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(user?.role) ? '/kanzlei/eingang' : '/dashboard') : '/login'}
            className="lg:hidden inline-flex items-center gap-2 mr-3 px-3 py-1.5 rounded-full bg-[#1B3A5C] text-white text-xs font-medium"
            aria-label={isAuthenticated ? t('nav.portal', 'Portal') : t('nav.login')}
            data-testid="mobile-login-button"
          >
            <User className="w-4 h-4" />
            <span>{isAuthenticated ? initials : t('nav.login')}</span>
          </Link>
          <button
            className="lg:hidden text-[#1B3A5C]"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <nav className="lf-site-nav hidden lg:flex items-center justify-end gap-7 pb-3">
          {renderNavLink(topNavLinks[0])}
          {renderNavLink(topNavLinks[1])}

          {/* Rechtsgebiete Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 text-sm font-medium tracking-wide hover:text-[#b8893d] transition-colors duration-200 relative group ${
                isRechtsgebieteActive || dropdownOpen ? 'text-[#b8893d]' : 'text-[#1B3A5C]'
              }`}
            >
              {t('nav.rechtsgebiete').toUpperCase()}
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
              />
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#b8893d] group-hover:w-full transition-all duration-300"></span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full right-0 mt-3 w-[420px] bg-white shadow-2xl border-t-4 border-[#b8893d] rounded-sm overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                <div className="bg-[#1B3A5C] px-5 py-3">
                  <p className="text-white text-xs tracking-[0.2em] font-semibold">UNSERE RECHTSGEBIETE</p>
                </div>
                <ul className="py-2 max-h-[480px] overflow-y-auto">
                  {rechtsgebieteItems.map((item) => (
                    <li key={item.key || item.label}>
                      <Link
                        to={item.href}
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-start gap-3 px-5 py-3 hover:bg-[#f5f8fa] border-l-2 border-transparent hover:border-[#b8893d] transition-all group"
                      >
                        <div className="w-9 h-9 bg-[#f5f8fa] group-hover:bg-[#b8893d] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
                          <item.icon className="w-4 h-4 text-[#1B3A5C] group-hover:text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[#1B3A5C] font-medium text-sm">{t(item.label)}</p>
                            {item.badge && (
                              <span className="text-[10px] tracking-wider font-bold bg-[#b8893d] text-white px-1.5 py-0.5 rounded-sm">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-[#34536b] text-xs mt-0.5 leading-snug">{t(item.desc)}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {renderNavLink(topNavLinks[2])}
          {topNavLinks[3] && renderNavLink(topNavLinks[3])}

          {/* Services Dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              onClick={() => setServicesOpen(!servicesOpen)}
              className={`flex items-center gap-1 text-sm font-medium tracking-wide hover:text-[#b8893d] transition-colors duration-200 relative group ${
                servicesOpen ? 'text-[#b8893d]' : 'text-[#1B3A5C]'
              }`}
            >
              {t('nav.services').toUpperCase()}
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
              />
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#b8893d] group-hover:w-full transition-all duration-300"></span>
            </button>

            {servicesOpen && (
              <div className="absolute top-full right-0 mt-3 w-[360px] bg-white shadow-2xl border-t-4 border-[#b8893d] rounded-sm overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                <div className="bg-[#1B3A5C] px-5 py-3">
                  <p className="text-white text-xs tracking-[0.2em] font-semibold">UNSERE SERVICES</p>
                </div>
                <ul className="py-2">
                  {servicesItems.map((item) => {
                    const isHash = item.href.includes('#');
                    const Wrapper = isHash ? 'a' : Link;
                    const wrapperProps = isHash ? { href: item.href } : { to: item.href };
                    return (
                      <li key={item.key || item.label}>
                        <Wrapper
                          {...wrapperProps}
                          onClick={() => setServicesOpen(false)}
                          className="flex items-start gap-3 px-5 py-3 hover:bg-[#f5f8fa] border-l-2 border-transparent hover:border-[#b8893d] transition-all group"
                        >
                          <div className="w-9 h-9 bg-[#f5f8fa] group-hover:bg-[#b8893d] rounded-sm flex items-center justify-center flex-shrink-0 transition-colors">
                            <item.icon className="w-4 h-4 text-[#1B3A5C] group-hover:text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[#1B3A5C] font-medium text-sm">{t(item.label)}</p>
                            <p className="text-[#34536b] text-xs mt-0.5 leading-snug">{t(item.desc)}</p>
                          </div>
                        </Wrapper>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </nav>

        {mobileOpen && (
          <nav className="lg:hidden border-t border-gray-100 py-4 flex flex-col gap-2">
            {topNavLinks.slice(0, 2).map((link) => (
              <div key={link.label} className="py-1">
                {renderNavLink(link, () => setMobileOpen(false))}
              </div>
            ))}

            <div className="py-1">
              <button
                onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                className="w-full flex items-center justify-between text-sm font-medium text-[#1B3A5C] tracking-wide"
              >
                <span>{t('nav.rechtsgebiete').toUpperCase()}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${mobileDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileDropdownOpen && (
                <ul className="mt-2 ml-2 border-l-2 border-[#b8893d]/30 pl-3 space-y-2">
                  {rechtsgebieteItems.map((item) => (
                    <li key={item.key || item.label}>
                      <Link
                        to={item.href}
                        onClick={() => {
                          setMobileOpen(false);
                          setMobileDropdownOpen(false);
                        }}
                        className="flex items-center gap-2 text-sm text-[#34536b] hover:text-[#b8893d]"
                      >
                        <item.icon className="w-3.5 h-3.5 text-[#b8893d]" />
                        {t(item.label)}
                        {item.badge && (
                          <span className="text-[9px] bg-[#b8893d] text-white px-1 py-0.5 rounded-sm">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {topNavLinks.slice(2).map((link) => (
              <div key={link.label} className="py-1">
                {renderNavLink(link, () => setMobileOpen(false))}
              </div>
            ))}

            <div className="py-1">
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between text-sm font-medium text-[#1B3A5C] tracking-wide"
              >
                <span>{t('nav.services').toUpperCase()}</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileServicesOpen && (
                <ul className="mt-2 ml-2 border-l-2 border-[#b8893d]/30 pl-3 space-y-2">
                  {servicesItems.map((item) => {
                    const isHash = item.href.includes('#');
                    const Wrapper = isHash ? 'a' : Link;
                    const wrapperProps = isHash ? { href: item.href } : { to: item.href };
                    return (
                      <li key={item.key || item.label}>
                        <Wrapper
                          {...wrapperProps}
                          onClick={() => {
                            setMobileOpen(false);
                            setMobileServicesOpen(false);
                          }}
                          className="flex items-center gap-2 text-sm text-[#34536b] hover:text-[#b8893d]"
                        >
                          <item.icon className="w-3.5 h-3.5 text-[#b8893d]" />
                          {t(item.label)}
                        </Wrapper>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {isAuthenticated ? (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs text-[#34536b] mb-2">Angemeldet als <span className="text-[#1B3A5C] font-medium">{user?.email}</span></p>
                <div className="flex flex-col gap-2">
                  {menuItems.map((item) => (
                    <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)} className="text-sm text-[#1B3A5C] flex items-center gap-2" data-testid={`mobile-menu-${item.id}`}>
                      <item.icon className="w-4 h-4 text-[#b8893d]" /> {item.label}
                    </Link>
                  ))}
                  <button onClick={handleLogout} data-testid="mobile-menu-logout" className="text-sm text-rose-600 flex items-center gap-2 text-left">
                    <LogOut className="w-4 h-4" /> {t('nav.logout')}
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 text-[#1B3A5C] mt-2">
                <span className="w-8 h-8 rounded-full bg-[#1B3A5C] flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </span>
                <span className="text-sm font-medium">Anmelden</span>
              </Link>
            )}

            {/* Mobile Sprachschalter */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-[10px] tracking-[0.2em] text-[#b8893d] mb-2 uppercase">
                {t('common.language', 'Sprache')}
              </p>
              <LanguageSwitcher variant="mobile" onSelect={() => setMobileOpen(false)} />
            </div>
          </nav>
        )}
      </div>
      {(!user || user.role === 'user') && <SchliesszeitBalken />}
    </header>
  );
};

export default Header;
